import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import type { AnalyticsHistoryResponse, AnalyticsSummaryResponse } from '../types/analytics';
// import { useAuth } from './auth';

export interface ActionMetadata {
  sourcePage?: string;
  targetPage?: string;
  searchQuery?: string;
  filterParams?: any;
  deviceType?: string; // 'desktop' | 'mobile' | 'tablet'
  userAgent?: string;
  durationSeconds?: number;
  [key: string]: any;
}

export const useAnalytics = () => {
  /* const { user } = useAuth(); // Not needed if token handles auth */
  const location = useLocation();

  const trackAction = useCallback(
    async (
      actionType: string,
      entityType?: string,
      entityId?: string,
      metadata: ActionMetadata = {},
    ) => {
      // Basic device type detection
      const width = window.innerWidth;
      const deviceType = width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop';

      try {
        await apiClient.post('analytics/action', {
          actionType,
          entityType,
          entityId,
          metadata: {
            ...metadata,
            sourcePage: metadata.sourcePage || location.pathname,
            userAgent: navigator.userAgent,
            deviceType,
          },
        });
      } catch (error) {
        console.warn('[Analytics] Failed to track action:', error);
        // Suppress errors to not impact user experience
      }
    },
    [location.pathname],
  );

  const trackPageView = useCallback(
    async (path: string) => {
      await trackAction('view', 'page', path, {
        targetPage: path,
      });
    },
    [trackAction],
  );

  return {
    trackAction,
    trackPageView,
  };
};

export const useAnalyticsSummary = (timeRange: '24h' | '7d' | '30d') => {
  return useQuery<AnalyticsSummaryResponse>({
    queryKey: ['analytics', 'summary', timeRange],
    queryFn: async () => {
      return apiClient.get<AnalyticsSummaryResponse>('analytics/summary', {
        params: { timeRange },
      });
    },
  });
};

export const useAnalyticsHistory = (timeRange: '24h' | '7d' | '30d') => {
  return useQuery<AnalyticsHistoryResponse>({
    queryKey: ['analytics', 'history', timeRange],
    queryFn: async () => {
      return apiClient.get<AnalyticsHistoryResponse>('analytics/history', {
        params: { timeRange },
      });
    },
  });
};
